import { useMemo } from 'react';
import { captureException } from '@sentry/react';
import { useQuery } from '@tanstack/react-query';
import { isAxiosError } from 'axios';

import { authorizedAxiosInstance } from '@/clients';

import { normalizeFinancialStatementGrowthResponse, normalizeRevenueSegmentResponse } from './revenueSegments';
import type {
    FinancialGrowthSection,
    FinancialStatementGrowthKind,
    FinancialStatementPeriodKind,
    FinancialStatementRowGroup,
} from './revenueSegments';

interface UseRevenueSegmentsArgs {
    enabled: boolean;
    growthSection?: FinancialGrowthSection;
    includeRevenueSegments: boolean;
    limit: number;
    parentLineItems: string[];
    periodKind: FinancialStatementPeriodKind;
    statementKind?: FinancialStatementGrowthKind;
    symbol?: string;
}

const emptyFinancialRowGroups: FinancialStatementRowGroup[] = [];
const FINANCIAL_ENRICHMENT_STALE_TIME = 15 * 60_000;
const retryFinancialEnrichment = (failureCount: number, error: unknown) => {
    const status = isAxiosError(error) ? error.response?.status : undefined;

    return failureCount < 1 && !(status && status >= 400 && status < 500);
};

const requestFinancialEnrichment = async (url: string, signal: AbortSignal, params: Record<string, unknown>) => {
    try {
        const response = await authorizedAxiosInstance.get<unknown>(url, {
            params,
            signal,
            timeout: 10_000,
        });

        return response.data;
    } catch (error) {
        if (!signal.aborted) {
            captureException(error);
        }

        throw error;
    }
};

export const useRevenueSegments = ({
    enabled,
    growthSection,
    includeRevenueSegments,
    limit,
    parentLineItems,
    periodKind,
    statementKind,
    symbol,
}: UseRevenueSegmentsArgs) => {
    const resolvedGrowthSection = growthSection ?? statementKind;
    const queryContext = [symbol, periodKind, limit] as const;
    const statementGrowthQuery = useQuery({
        enabled: enabled && Boolean(statementKind) && Boolean(symbol),
        queryFn: ({ signal }) =>
            requestFinancialEnrichment(`/api/vendors/fmp/financials/${symbol}/growth/${statementKind}`, signal, {
                limit,
                period: periodKind,
            }),
        queryKey: ['vendors', 'fmp', 'statement-growth', statementKind, ...queryContext],
        retry: retryFinancialEnrichment,
        staleTime: FINANCIAL_ENRICHMENT_STALE_TIME,
    });
    const consolidatedGrowthQuery = useQuery({
        enabled: enabled && Boolean(resolvedGrowthSection) && Boolean(symbol),
        queryFn: ({ signal }) =>
            requestFinancialEnrichment(`/api/vendors/fmp/financials/analysis/${symbol}/growth`, signal, {
                limit,
                period: periodKind,
            }),
        queryKey: ['vendors', 'fmp', 'consolidated-growth', ...queryContext],
        retry: retryFinancialEnrichment,
        staleTime: FINANCIAL_ENRICHMENT_STALE_TIME,
    });
    const productSegmentsQuery = useQuery({
        enabled: enabled && includeRevenueSegments && Boolean(symbol),
        queryFn: ({ signal }) =>
            requestFinancialEnrichment('/api/vendors/fmp/segments/revenue/products', signal, {
                period: periodKind,
                structure: 'flat',
                symbol,
            }),
        queryKey: ['vendors', 'fmp', 'revenue-segments', 'products', symbol, periodKind],
        retry: retryFinancialEnrichment,
        staleTime: FINANCIAL_ENRICHMENT_STALE_TIME,
    });
    const geographySegmentsQuery = useQuery({
        enabled: enabled && includeRevenueSegments && Boolean(symbol),
        queryFn: ({ signal }) =>
            requestFinancialEnrichment('/api/vendors/fmp/segments/revenue/geography', signal, {
                period: periodKind,
                structure: 'flat',
                symbol,
            }),
        queryKey: ['vendors', 'fmp', 'revenue-segments', 'geography', symbol, periodKind],
        retry: retryFinancialEnrichment,
        staleTime: FINANCIAL_ENRICHMENT_STALE_TIME,
    });

    return useMemo<FinancialStatementRowGroup[]>(() => {
        if (!enabled || !resolvedGrowthSection || !symbol) {
            return emptyFinancialRowGroups;
        }

        const segmentGroups = includeRevenueSegments
            ? [
                  normalizeRevenueSegmentResponse({
                      kind: 'product',
                      periodKind,
                      response: productSegmentsQuery.data ?? [],
                  }),
                  normalizeRevenueSegmentResponse({
                      kind: 'geography',
                      periodKind,
                      response: geographySegmentsQuery.data ?? [],
                  }),
              ]
            : [];

        return [
            ...segmentGroups,
            ...normalizeFinancialStatementGrowthResponse({
                consolidatedResponse: consolidatedGrowthQuery.data ?? [],
                growthSection: resolvedGrowthSection,
                parentLineItems,
                periodKind,
                response: statementGrowthQuery.data ?? [],
            }),
        ];
    }, [
        consolidatedGrowthQuery.data,
        enabled,
        geographySegmentsQuery.data,
        includeRevenueSegments,
        parentLineItems,
        periodKind,
        productSegmentsQuery.data,
        resolvedGrowthSection,
        statementGrowthQuery.data,
        symbol,
    ]);
};
